/* eslint-disable max-len */
import React, { useMemo } from 'react';
import cn from 'classnames';
import { useCartMutationInBasket } from 'app/views/Basket/hooks';
import { makeFlatSection } from 'app/views/Basket/BasketListArea';
import {
    gql, useFragment, useQuery, useReactiveVar,
} from '@apollo/client';
import { useToastExecuter } from 'app/hooks/useToastExecuter';
import { useDishPrice } from 'app/hooks/dish';
import { useDishDetails } from 'app/components/MobileDishDetails';
import { formatDate } from 'app/utils/date';
import { analyticService } from 'global/services';
import { selectedPeriodVar, subscriptionTypeVar } from 'app/apollo/reaction';
import css from './basketTopDishCard.module.scss';

import {
    BASKET_TOP_DISH_CART_SECTIONS_WITH_DISHES,
    BASKET_PAGE_DELIVERY_CART_SECTIONS_WITH_DISHES,
} from '../../../../graphql/basketListArea.graphql';

import { topDishes } from './topDishesRecord';
import {
    getDishAddedPeriod,
    removeAddedDish,
    saveDishAddedPeriod,
} from './addedPeriodStorage';

import DividerIcon from './divider.component.svg';
import PlusIcon from './plus.component.svg';
import MinusIcon from './minus.component.svg';

interface IBasketTopDishCardProps {
    id: string;
    isDishInCurrentPeriod?: boolean;
    targetPeriod?: string;
    isTargetPeriodLoading?: boolean;
    deliveryPeriod?: string;
}
interface IBasketTopDishCardButtonProps {
    id: string;
    handleShowToast: () => void
    period?: string;
    isDisabled?: boolean;
    isDeliveryBasket: boolean;
}

const isDeliveryBasketPage = () => window.location.pathname.includes('app/delivery');

/**
 * @description работаем ли мы с корзиной доставки.
 *
 * Если доставка в том же цикле, что и блюдо, отдельный инвойс не нужен —
 * блюдо просто добавляется в корзину доставки. Если циклы разные, блюдо
 * уходит в обычную корзину на targetPeriod, и инвойс создастся при сохранении.
 */
const shouldUseDeliveryCart = (deliveryPeriod?: string, targetPeriod?: string) => (
    isDeliveryBasketPage() && Boolean(deliveryPeriod) && deliveryPeriod === targetPeriod
);

const BasketTopDishCardButton = ({
    id, handleShowToast, period = undefined, isDisabled = false, isDeliveryBasket,
}: IBasketTopDishCardButtonProps) => {
    const deliveryId = window.location.pathname.split('/').find((e) => Boolean(e) && !Number.isNaN(Number(e)));
    const currentPeriod = useReactiveVar(selectedPeriodVar);
    const {
        isCartUpdating,
        mutateItemInCart,
    } = useCartMutationInBasket(isDeliveryBasket ? { delivery_Id: deliveryId } : undefined);

    /* DA */
    const cartVariables = useMemo(() => {
        if (isDeliveryBasket) {
            return { delivery_id: deliveryId };
        }

        return { period: period || currentPeriod };
    }, [currentPeriod, deliveryId, isDeliveryBasket, period]);

    const { data, loading, refetch } = useQuery<any>(isDeliveryBasket
        ? BASKET_PAGE_DELIVERY_CART_SECTIONS_WITH_DISHES
        : BASKET_TOP_DISH_CART_SECTIONS_WITH_DISHES,
        {
            fetchPolicy: 'cache-first',
            variables: cartVariables,
        });

    const menuDish = useFragment({
        fragment: gql`
            fragment TOP_CARD_MENU_DISH on menuDish {
                id,
                price,
                portions,
                title
                caption
                category_id
                category_description
                subcategory_id
                subcategory_name
            }
        `,
        from: {
            __ref: `menuDish:${id}`,
        },
    });

    /* SELECTORS */
    const price = useDishPrice({ id });

    const sections = useMemo(
        () => (isDeliveryBasket ? data?.deliveryCart?.sections : data?.cart?.sections),
        [isDeliveryBasket, data],
    );

    const flatSection = useMemo(
        () => makeFlatSection(sections),
        [sections],
    );

    const inBasket = useMemo(
        () => flatSection.some((item: any) => item?.dish_id === id),
        [flatSection, id],
    );

    const portionStep = useMemo(() => {
        const [portion] = menuDish?.data?.portions || [0];
        return portion;
    }, [menuDish?.data?.portions],
    );

    const portionCount = useMemo(
        () => {
            const item = flatSection.find((elm: any) => (elm?.dish_id === id));
            return item?.portions || 0;
        },
        [flatSection, id],
    );

    const disabled = useMemo(
        () => (loading || isCartUpdating || isDisabled),
        [isCartUpdating, isDisabled, loading],
    );

    const disabledForPlus = useMemo(
        () => {
            // eslint-disable-next-line no-unsafe-optional-chaining
            const R = (menuDish?.data?.portions?.length - 1) || -1;
            return R < 0
                ? false
                : menuDish?.data?.portions[R] <= portionCount;
        },
        [menuDish?.data?.portions, portionCount],
    );

    const uiPortionText = useMemo(
        () => (isDisabled ? '' : (portionCount || portionStep)),
        [isDisabled, portionCount, portionStep],
    );

    /* HANDLERS */

    const addToBasket = (event: any, action: string) => {
        const portions = action === 'plus'
            ? (portionCount + portionStep)
            : (portionCount - portionStep);
        mutateItemInCart({
            dishes: [{ dish_id: id, portions }],
            period,
            // @ts-expect-error
            optimistic: false,
            /* @note мутация обновляет сущность cart:new:<period> в кэше, но поле
             * Query.cart({period}) для чужого цикла остаётся прежним. Без рефетча
             * кнопка не подхватит порции, пока блюдо добавлено не в текущий цикл. */
            /* @note запись доп. цикла меняем только по факту: блюдо ушло из
             * корзины цикла (portions → 0) — запись выкидываем, блюдо осталось —
             * сохраняем. Так запись не переживает ни ошибку мутации, ни удаление
             * топ-блюда кнопкой «минус», и notifier не висит на пустой корзине */
            onCompletedCallback: () => {
                if (portions <= 0) {
                    if (getDishAddedPeriod(id) === period) {
                        removeAddedDish(id);
                    }
                } else if (period && period !== currentPeriod) {
                    saveDishAddedPeriod(id, period);
                }

                refetch();
            },
        });
        analyticService.push({
            eventName: 'add_to_cart',
            dishes: [{
                ...menuDish?.data,
                categoryId: menuDish?.data?.category_id,
                portion: portions,
            }],
            source: '/basket',
            subscriptionType: subscriptionTypeVar(),
            typeOfSet: 'regular',
        });
    };


    /* STL */
    const topDishCardButtonRoot = cn({
        [css.topDishCardButtonRoot]: true,
        [css.inBasket]: inBasket,
    });

    const centralItemRootClasses = cn({
        [css.centralItemRootClasses]: true,
        [css.inBasket]: inBasket,
    });

    const leftItemClasses = cn({
        [css.cornerItemRootClasses]: true,
        [css.left]: true,
    });

    const rigthItemClasses = cn({
        [css.cornerItemRootClasses]: true,
        [css.right]: true,
    });

    return (
        <div
            className={topDishCardButtonRoot}
            onClick={(e) => e.stopPropagation()}
            role="none"
        >
            <div className={css.topDishCardButtonWrapper}>
                {/* MINUS */}
                {inBasket && (
                    <button
                        className={leftItemClasses}
                        onClick={(e) => {
                            addToBasket(e, 'minus');
                        }}
                        disabled={disabled}
                        type="button"
                    >
                        <div
                            className={css.minusIcon}
                        >
                            <MinusIcon />
                        </div>
                    </button>
                )}
                {/*  */}
                <button
                    onClick={(e) => {
                        addToBasket(e, 'plus');
                        handleShowToast();
                    }}
                    type="button"
                    disabled={disabled || inBasket}
                    className={centralItemRootClasses}
                >
                    <div className={css.step}>
                        {uiPortionText}
                    </div>
                    <div className={css.divider}>
                        <DividerIcon />
                    </div>
                    <div className={css.price}>
                        {isDisabled ? '' : price}
                    </div>
                </button>
                {/* PLUS */}
                {inBasket && (
                    <button
                        className={rigthItemClasses}
                        onClick={(e) => addToBasket(e, 'plus')}
                        disabled={disabled || disabledForPlus}
                        type="button"
                    >
                        <div className={css.plusIcon}>
                            <PlusIcon />
                        </div>
                    </button>
                )}
            </div>
        </div>
    );
};

export const BasketTopDishCard = ({
    id,
    isDishInCurrentPeriod = false,
    targetPeriod = undefined,
    isTargetPeriodLoading = false,
    deliveryPeriod = undefined,
}: IBasketTopDishCardProps) => {
    const { title: { firstLine, secondLine }, description } = topDishes[id];
    const [handleShowToast] = useToastExecuter({ customEventName: 'executeBasketToastNotification' });
    const { openDishDetails } = useDishDetails();

    /* SELECTORS */
    const isDeliveryBasket = shouldUseDeliveryCart(deliveryPeriod, targetPeriod);

    /* Блюдо может отсутствовать в выбранном периоде, но цена и порции
    берутся из первого периода, в котором блюдо доступно */
    const periodForAdding = useMemo(
        () => (isDeliveryBasket ? undefined : targetPeriod),
        [isDeliveryBasket, targetPeriod],
    );

    // const periodHint = useMemo(() => {
    //     if (!periodForAdding || isDishInCurrentPeriod) return null;
    //     return `Добавим в меню с ${formatDate(periodForAdding)}`;
    // }, [isDishInCurrentPeriod, periodForAdding]);

    const isButtonDisabled = isTargetPeriodLoading
        || (!isDeliveryBasket && !periodForAdding);

    const handleSendAnalytics = () => {
        analyticService.push({
            eventName: 'Upsell_Click',
            upsell_type: 'promo',
            banner_name: 'legend',
            source_name: 'basket',
        });
    };

    const backgroundClasses = cn({
        [css.basketTopDishCardRoot]: true,
        [css[`top_${id}`]]: true,
    });

    const titleClasses = cn({
        [css.basketTopDishCardTitle]: true,
        [css[`top_${id}`]]: true,
    });

    const descriptionClasses = cn({
        [css.basketTopDishCardDescription]: true,
        [css[`top_${id}`]]: true,
    });
    return (
        <div
            onClick={() => {
                openDishDetails(id);
                handleSendAnalytics();
            }}
            className={css.basketTopDishRootWrapper}
            role="none"
        >
            <div className={backgroundClasses}>
                <div className={css.basketTopDishContentContainer}>
                    {/* <div className={css.basketTopDishShieldWrapper}>
                        <TextShield
                            name="legend"
                            position="static"
                        />
                    </div> */}
                    <div className={titleClasses}>
                        {firstLine}
                        <br />
                        {secondLine}
                    </div>
                    <div className={descriptionClasses}>
                        {description}
                    </div>
                    <BasketTopDishCardButton
                        id={id}
                        handleShowToast={handleShowToast}
                        period={periodForAdding}
                        isDisabled={isButtonDisabled}
                        isDeliveryBasket={isDeliveryBasket}
                    />
                </div>
            </div>
        </div>
    );
};
